import { useEffect, useState } from 'react'
import { optionalNumber } from '@/components/add-property-dialog/schema'
import { type Resolver } from 'react-hook-form'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Form, FormControl, FormMessage, FormDialog, FormField, FormItem, FormLabel,
	Input,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useListPropertiesQuery, useUpdateDealMutation } from '@/api'
import { formatPropertyAddress } from '@/lib/property-address'
import { usePersistedForm } from '@/lib/use-persisted-form'
import { extractErrorMessage } from '@/lib/error-message'
import type { Deal } from '@/types'
import { CurrencyField } from '@/components/currency-field'

const editDealSchema = z.object({
	propertyId: z.string().optional(),
	// `optionalNumber`, а не `z.coerce.number()`: пустая строка приводится к
	// нулю, и очистить сумму было нельзя — сделка становилась нулевой.
	amount:     optionalNumber(z.coerce.number().nonnegative()),
	// No currency here: the picker is gone and the deal keeps its stored
	// currency. Keeping the field meant a rehydrated draft could silently
	// PATCH a currency different from the one the read-only field shows.
	startDate:  z.string().optional(),
	endDate:    z.string().optional(),
})

type EditDealFormValues = z.infer<typeof editDealSchema>

interface EditDealDialogProps {
	deal: Deal
	open: boolean
	onOpenChange: (open: boolean) => void
}

function dateOnly(iso?: string | null): string {
	if (!iso) return ''
	return iso.slice(0, 10)
}

export function EditDealDialog({ deal, open, onOpenChange }: EditDealDialogProps) {
	const { t } = useTranslation()
	const { data: propertiesData } = useListPropertiesQuery({ limit: 200 })
	const [updateDeal, { isLoading }] = useUpdateDealMutation()
	const [hasError, setHasError] = useState(false)
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const { form, resetDraft } = usePersistedForm<EditDealFormValues>({
		key:           `deal:edit:${deal.id}`,
		recordVersion: deal.updatedAt,
		defaultValues: {
			propertyId: deal.propertyId ?? undefined,
			amount:     deal.amount != null ? Number(deal.amount) : undefined,
			startDate:  dateOnly(deal.startDate),
			endDate:    dateOnly(deal.endDate),
		},
		resolver: zodResolverI18n(editDealSchema) as Resolver<EditDealFormValues>,
		mode:     'onBlur',
		active:   open,
	})

	// The parent keeps this dialog mounted, so error state would otherwise
	// survive close/reopen — clear it whenever the dialog opens.
	useEffect(() => {
		if (open) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasError(false)
			setErrorMsg(undefined)
		}
	}, [open])

	const properties = propertiesData?.properties ?? []

	async function onSubmit(values: EditDealFormValues) {
		setHasError(false)
		setErrorMsg(undefined)
		try {
			await updateDeal({
				id: deal.id,
				body: {
					propertyId: values.propertyId || undefined,
					amount:     values.amount != null ? String(values.amount) : undefined,
					startDate:  values.startDate || undefined,
					endDate:    values.endDate   || undefined,
				},
			}).unwrap()
			resetDraft()
			onOpenChange(false)
		} catch (err) {
			setHasError(true)
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={onOpenChange}
				title={t('deals.editDialog.title')}
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel={isLoading ? t('common.saving') : t('deals.editDialog.submit')}
				cancelLabel={t('deals.editDialog.cancel')}
				closeAriaLabel={t('common.close')}
				isSubmitting={isLoading}
			>
				{hasError && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>{t('deals.editDialog.errors.save')}</AlertTitle>
						{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
					</Alert>
				)}

				<FormField
					control={form.control}
					name="propertyId"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('deals.editDialog.property')}</FormLabel>
							<Select onValueChange={field.onChange} value={field.value}>
								<FormControl>
									<SelectTrigger width="full">
										<SelectValue placeholder={t('deals.editDialog.propertyPlaceholder')} />
									</SelectTrigger>
								</FormControl>
								<SelectContent>
									{properties.map((p) => (
										<SelectItem key={p.id} value={p.id}>
											{formatPropertyAddress(p.city, p.address)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="amount"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.editDialog.amount')}</FormLabel>
								<FormControl>
									<Input type="number" min={0} step="100" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<CurrencyField label={t('deals.editDialog.currency')} value={deal.currency} />
				</div>

				<div className="grid grid-cols-2 gap-3">
					<FormField
						control={form.control}
						name="startDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.editDialog.startDate')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="endDate"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('deals.editDialog.endDate')}</FormLabel>
								<FormControl>
									<Input type="date" {...field} value={field.value ?? ''} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			</FormDialog>
		</Form>
	)
}
